import {
  PaymentRequestButtonElement,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  PaymentRequest as StripePaymentRequest,
  PaymentRequestPaymentMethodEvent,
} from "@stripe/stripe-js";
import { useEffect, useRef, useState } from "react";
import { useTestAttrsFactory } from "utils/testid";

const GOOGLE_PAY_LOG_PREFIX = "[Stripe Google Pay][button]";

export const DisabledGooglePayButton = () => {
  const testAttrs = useTestAttrsFactory();

  return (
    <div className="w-full mb-5">

    <button
      type="button"
      disabled
      aria-label="Google Pay unavailable until a valid amount is entered"
      className="flex h-10 w-full cursor-not-allowed appearance-none items-center justify-center gap-1 rounded-[5px] border-0 bg-[#000000] "
      {...testAttrs("google-pay-disabled", "Google Pay unavailable")}
    >
      <svg
        aria-hidden="true"
        className="h-[18px] w-[18px] shrink-0"
        viewBox="0 0 18 18"
      >
        <path
          fill="#4285F4"
          d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.797 2.716v2.258h2.909c1.702-1.567 2.684-3.874 2.684-6.614Z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.835.859-3.047.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z"
        />
        <path
          fill="#FBBC05"
          d="M3.963 10.706A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.168.281-1.706V4.962H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.038l3.007-2.332Z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.58C13.464.892 11.426 0 9 0A9 9 0 0 0 .956 4.962l3.007 2.332C4.672 5.165 6.656 3.58 9 3.58Z"
        />
      </svg>
      <span className="text-[17px] font-medium leading-none text-bgPrimary">
        Pay
      </span>
    </button>
    </div>
  );
};

interface GooglePayButtonProps {
  amount: number | string;
  clientSecret: string;
  orderId?: number;
  onIntentFailure?: (orderId: number, error: any) => Promise<void>;
  disabled?: boolean;
  allowZeroAmount?: boolean;
  requestLabel?: string;
  handleGooglePay: (success: boolean, intent?: unknown) => Promise<void> | void;
  setIsGooglePay: (isAvailable: boolean) => void;
}

const GooglePayButton = ({
  amount,
  clientSecret,
  orderId = 0,
  onIntentFailure = async () => {},
  disabled = false,
  allowZeroAmount = false,
  requestLabel = "Online Payment",
  handleGooglePay,
  setIsGooglePay,
}: GooglePayButtonProps) => {
  const stripe = useStripe();
  const testAttrs = useTestAttrsFactory();
  const disabledRef = useRef(disabled);
  const handleGooglePayRef = useRef(handleGooglePay);
  const isConfirmingRef = useRef(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [paymentRequest, setPaymentRequest] =
    useState<StripePaymentRequest | null>(null);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    handleGooglePayRef.current = handleGooglePay;
  }, [handleGooglePay]);

  useEffect(() => {
    console.info(`${GOOGLE_PAY_LOG_PREFIX} mounted`, {
      isFramed: window.self !== window.top,
      isSecureContext: window.isSecureContext,
      paymentRequestSupported: "PaymentRequest" in window,
    });

    return () => {
      console.info(`${GOOGLE_PAY_LOG_PREFIX} unmounted`);
    };
  }, []);

  useEffect(() => {
    if (!stripe) return;

    const normalizedAmount = Number(String(amount).replace(/,/g, ""));
    const amountInCents = Number.isFinite(normalizedAmount)
      ? Math.max(allowZeroAmount ? 0 : 100, Math.round(normalizedAmount * 100))
      : allowZeroAmount
        ? 0
        : 100;
    const request = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: requestLabel,
        amount: amountInCents,
      },
      requestPayerEmail: true,
      requestPayerName: true,
      requestPayerPhone: true,
    });
    let isActive = true;

    const handlePaymentMethod = async (
      event: PaymentRequestPaymentMethodEvent
    ) => {
      if (disabledRef.current || isConfirmingRef.current) {
        event.complete("fail");
        return;
      }

      isConfirmingRef.current = true;
      setIsConfirming(true);
      console.info(`${GOOGLE_PAY_LOG_PREFIX} confirming SetupIntent`);
      let stripeConfirmationSucceeded = false;

      try {
        const initialResult = await stripe.confirmCardSetup(
          clientSecret,
          { payment_method: event.paymentMethod.id },
          { handleActions: false }
        );

        if (initialResult.error || !initialResult.setupIntent) {
          await onIntentFailure(
            orderId,
            initialResult.error || new Error("Google Pay setup failed")
          );
          console.error(`${GOOGLE_PAY_LOG_PREFIX} SetupIntent failed`, {
            code: initialResult.error?.code,
            message: initialResult.error?.message,
            type: initialResult.error?.type,
          });
          event.complete("fail");
          return;
        }

        event.complete("success");
        let setupIntent = initialResult.setupIntent;

        if (setupIntent.status === "requires_action") {
          const actionResult = await stripe.confirmCardSetup(clientSecret);
          if (actionResult.error || !actionResult.setupIntent) {
            await onIntentFailure(
              orderId,
              actionResult.error || new Error("Google Pay setup failed")
            );
            console.error(
              `${GOOGLE_PAY_LOG_PREFIX} SetupIntent action failed`,
              {
                code: actionResult.error?.code,
                message: actionResult.error?.message,
                type: actionResult.error?.type,
              }
            );
            return;
          }
          setupIntent = actionResult.setupIntent;
        }

        console.info(`${GOOGLE_PAY_LOG_PREFIX} SetupIntent confirmed`, {
          hasPaymentMethod: Boolean(setupIntent.payment_method),
          status: setupIntent.status,
        });
        stripeConfirmationSucceeded = true;
        await handleGooglePayRef.current(true, setupIntent);
      } catch (error) {
        if (!stripeConfirmationSucceeded) {
          await onIntentFailure(orderId, error);
        }
        event.complete("fail");
        console.error(`${GOOGLE_PAY_LOG_PREFIX} confirmation exception`, {
          message: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : "UnknownError",
        });
      } finally {
        isConfirmingRef.current = false;
        setIsConfirming(false);
        console.info(`${GOOGLE_PAY_LOG_PREFIX} confirmation finished`);
      }
    };

    const handleCancel = () => {
      console.info(`${GOOGLE_PAY_LOG_PREFIX} wallet sheet cancelled`);
    };

    request.on("paymentmethod", handlePaymentMethod);
    request.on("cancel", handleCancel);
    console.info(`${GOOGLE_PAY_LOG_PREFIX} canMakePayment check started`, {
      amountIsValid: Number.isFinite(normalizedAmount),
    });

    request
      .canMakePayment()
      .then((result) => {
        if (!isActive) return;

        const googlePayAvailable = Boolean(result?.googlePay);
        const availableMethods = Object.entries(result || {})
          .filter(([, isAvailable]) => isAvailable)
          .map(([method]) => method);
        console.info(`${GOOGLE_PAY_LOG_PREFIX} canMakePayment result`, {
          applePayAvailable: Boolean(result?.applePay),
          availableMethods,
          canMakePayment: Boolean(result),
          googlePayAvailable,
        });

        setIsGooglePay(googlePayAvailable);
        setPaymentRequest(googlePayAvailable ? request : null);
      })
      .catch((error) => {
        if (!isActive) return;

        console.error(`${GOOGLE_PAY_LOG_PREFIX} canMakePayment failed`, {
          message: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : "UnknownError",
        });
        setIsGooglePay(false);
        setPaymentRequest(null);
      });

    return () => {
      isActive = false;
      request.off("paymentmethod", handlePaymentMethod);
      request.off("cancel", handleCancel);
    };
  }, [
    allowZeroAmount,
    amount,
    clientSecret,
    onIntentFailure,
    orderId,
    requestLabel,
    setIsGooglePay,
    stripe,
  ]);

  if (!paymentRequest) return null;

  return (
    <div
      aria-disabled={disabled || isConfirming}
      className={
        disabled || isConfirming
          ? "pointer-events-none cursor-not-allowed opacity-60 mb-5 "
          : "cursor-pointer mb-5 "
      }
      {...testAttrs("google-pay-stripe", "Google Pay")}
    >
      <PaymentRequestButtonElement
        options={{
          disableMultipleButtons: true,
          paymentRequest,
          style: {
            paymentRequestButton: {
              height: "40px",
              theme: "dark",
              type: "default",
            },
          },
        }}
        onClick={(event) => {
          if (disabled || isConfirming) event.preventDefault();
        }}
        onReady={() =>
          console.info(`${GOOGLE_PAY_LOG_PREFIX} payment button ready`)
        }
      />
    </div>
  );
};

export default GooglePayButton;
